import type { CampaignPlanSummary, CampaignPlanStageStrategy, CampaignFunnelOverviewItem } from "./campaign-draft";
import type { CampaignPlanPayload } from "./campaign-plan-payload";

export interface NormalizedCampaignPlan {
  executive_summary: string;
  funnel_overview: Array<{ stage: string; role: string; asset_count?: number }>;
  stage_strategy: Array<{
    stage: string;
    pitch: string;
    psychological_goal: string;
    messaging_strategy: string;
    copy_directions: string[];
    transition_to_next_stage: string;
  }>;
  why_this_campaign_wins: string[];
}

export interface CampaignPlanValidationResult {
  valid: boolean;
  errors: string[];
}

function extractPayload(raw: any): any {
  if (!raw || typeof raw !== "object") return raw;
  if (raw.executive_summary !== undefined) return raw;
  if (raw.output?.executive_summary !== undefined) return raw.output;
  if (raw.data?.executive_summary !== undefined) return raw.data;
  if (raw.result?.executive_summary !== undefined) return raw.result;
  if (raw.response?.executive_summary !== undefined) return raw.response;
  for (const key of Object.keys(raw)) {
    const val = raw[key];
    if (val && typeof val === "object" && val.executive_summary !== undefined) {
      return val;
    }
  }
  return raw;
}

function normalizeStringList(val: any): string[] {
  if (!val) return [];
  if (Array.isArray(val)) return val.map(String);
  if (typeof val === "string") return [val];
  return [];
}

function normalizeFunnelOverview(val: any): NormalizedCampaignPlan["funnel_overview"] {
  if (!Array.isArray(val)) return [];
  return val.map((item: any) => ({
    stage: item.stage ?? item.name ?? item.funnel_stage ?? "",
    role: item.role ?? item.objective ?? item.description ?? "",
    asset_count: item.asset_count ?? item.assetCount ?? item.assets ?? undefined,
  }));
}

function normalizeStageStrategy(val: any): NormalizedCampaignPlan["stage_strategy"] {
  if (!Array.isArray(val)) return [];
  return val.map((item: any) => ({
    stage: item.stage ?? item.stage_name ?? item.name ?? "",
    pitch: item.pitch ?? item.stage_pitch ?? item.description ?? "",
    psychological_goal:
      item.psychological_goal ?? item.psychologicalGoal ?? item.psych_goal ?? item.goal ?? "",
    messaging_strategy:
      item.messaging_strategy ?? item.messagingStrategy ?? item.messaging ?? "",
    copy_directions: normalizeStringList(
      item.copy_directions ?? item.copyDirections ?? item.directions ?? []
    ),
    transition_to_next_stage:
      item.transition_to_next_stage ??
      item.transitionToNextStage ??
      item.transition ??
      "",
  }));
}

export function normalizeCampaignPlanResponse(raw: any): NormalizedCampaignPlan {
  const data = extractPayload(raw);

  return {
    executive_summary: typeof data?.executive_summary === "string"
      ? data.executive_summary
      : typeof data?.executiveSummary === "string"
      ? data.executiveSummary
      : "",
    funnel_overview: normalizeFunnelOverview(
      data?.funnel_overview ?? data?.funnelOverview ?? []
    ),
    stage_strategy: normalizeStageStrategy(
      data?.stage_strategy ?? data?.stageStrategy ?? data?.stage_strategies ?? []
    ),
    why_this_campaign_wins: normalizeStringList(
      data?.why_this_campaign_wins ??
      data?.whyThisCampaignWins ??
      data?.why_wins ??
      []
    ),
  };
}

export function validateCampaignPlanResponse(
  normalized: NormalizedCampaignPlan
): CampaignPlanValidationResult {
  const errors: string[] = [];

  if (!normalized.executive_summary || typeof normalized.executive_summary !== "string") {
    errors.push("executive_summary is missing or not a string");
  }
  if (!Array.isArray(normalized.funnel_overview) || normalized.funnel_overview.length === 0) {
    errors.push("funnel_overview is missing or empty");
  }
  if (!Array.isArray(normalized.stage_strategy) || normalized.stage_strategy.length === 0) {
    errors.push("stage_strategy is missing or empty");
  }
  if (!Array.isArray(normalized.why_this_campaign_wins) || normalized.why_this_campaign_wins.length === 0) {
    errors.push("why_this_campaign_wins is missing or empty");
  }

  for (const item of normalized.funnel_overview) {
    if (!item.stage || !item.role) {
      errors.push(`funnel_overview item missing stage or role: ${JSON.stringify(item)}`);
      break;
    }
  }

  for (const item of normalized.stage_strategy) {
    if (!item.stage || !item.pitch) {
      errors.push(`stage_strategy item missing stage or pitch: ${JSON.stringify(item)}`);
      break;
    }
  }

  return { valid: errors.length === 0, errors };
}

export function mapToCampaignPlanSummary(
  normalized: NormalizedCampaignPlan,
  campaignName: string,
  mediaMixTotal?: number,
): CampaignPlanSummary {
  const rawSum = normalized.funnel_overview.reduce(
    (sum, f) => sum + (f.asset_count ?? 0),
    0
  );

  const targetTotal = mediaMixTotal && mediaMixTotal > 0 ? mediaMixTotal : rawSum;

  let scaledCounts: number[] = [];
  if (targetTotal > 0 && rawSum > 0 && targetTotal !== rawSum) {
    const rawCounts = normalized.funnel_overview.map((f) => f.asset_count ?? 0);
    const floats = rawCounts.map((c) => (c / rawSum) * targetTotal);
    scaledCounts = floats.map((f) => Math.round(f));
    let diff = targetTotal - scaledCounts.reduce((a, b) => a + b, 0);
    const sortedIndices = [...scaledCounts.keys()].sort(
      (a, b) => (floats[b] - scaledCounts[b]) - (floats[a] - scaledCounts[a])
    );
    for (const idx of sortedIndices) {
      if (diff === 0) break;
      if (diff > 0) { scaledCounts[idx]++; diff--; }
      else if (scaledCounts[idx] > 0) { scaledCounts[idx]--; diff++; }
    }
  }

  const funnelOverview: CampaignFunnelOverviewItem[] = normalized.funnel_overview.map((f, i) => ({
    stage: f.stage,
    role: f.role,
    assetCount: scaledCounts.length > 0 ? scaledCounts[i] : f.asset_count,
  }));

  const stageStrategy: CampaignPlanStageStrategy[] = normalized.stage_strategy.map((s) => ({
    stage: s.stage,
    pitch: s.pitch,
    psychologicalGoal: s.psychological_goal,
    messagingStrategy: s.messaging_strategy,
    copyDirections: s.copy_directions,
    transitionToNextStage: s.transition_to_next_stage,
  }));

  const totalAdUnits = funnelOverview.reduce(
    (sum, f) => sum + (f.assetCount ?? 0),
    0
  );

  return {
    id: `plan-${Date.now()}`,
    name: campaignName,
    stages: [],
    totalAdUnits,
    status: "generated",
    executiveSummary: normalized.executive_summary,
    funnelOverview,
    stageStrategy,
    whyThisCampaignWins: normalized.why_this_campaign_wins,
    compiledAt: new Date().toISOString(),
    error: null,
  };
}

export async function generateCampaignPlanReport(
  payload: CampaignPlanPayload,
  campaignName: string,
  mediaMixTotal?: number,
): Promise<CampaignPlanSummary> {
  if (process.env.NODE_ENV !== "production") {
    console.log("[CampaignPlan] Sending payload to /api/campaign-plan");
    console.log("[CampaignPlan] Top-level keys:", Object.keys(payload));
  }

  const res = await fetch("/api/campaign-plan", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    let errMsg = `Server returned ${res.status}`;
    try {
      const errBody = await res.json();
      errMsg = errBody?.message ?? errMsg;
    } catch {}
    throw new Error(errMsg);
  }

  const rawJson = await res.json();

  if (process.env.NODE_ENV !== "production") {
    console.log("[CampaignPlan] Raw response top-level keys:", Object.keys(rawJson ?? {}));
  }

  const normalized = normalizeCampaignPlanResponse(rawJson);
  const validation = validateCampaignPlanResponse(normalized);

  if (!validation.valid) {
    console.warn("[CampaignPlan] Validation issues:", validation.errors);
    console.warn("[CampaignPlan] Raw response (first 2000 chars):", JSON.stringify(rawJson).slice(0, 2000));
    if (!normalized.executive_summary && !normalized.stage_strategy.length && !normalized.funnel_overview.length) {
      throw new Error("Campaign plan response was empty or unrecognized");
    }
  }

  return mapToCampaignPlanSummary(normalized, campaignName, mediaMixTotal);
}
